import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBuildProjects } from "../store/slices/buildProjectsSlice";
import { Section, Container, SectionEyebrow } from "../components/ui/Section";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  scaleIn,
} from "../components/ui/Motion";
import { mediaUrl } from "../utils/media";
import {
  PageHeader,
  Title,
  HeaderSub,
  IntroGrid,
  Lead,
  IntroImage,
  StatsRow,
  StatItem,
  ServiceList,
  ServiceItem,
  StepsGrid,
  StepCard,
  StepGhost,
  StepNumber,
  StepTitle,
  StepText,
  ProjectsGrid,
  ProjectCard,
  ProjectImage,
  ProjectBody,
  ProjectMeta,
  ProjectTitle,
  ProjectDesc,
  CtaSection,
  Cta,
  CtaTitle,
  CtaText,
  CtaButton,
} from "./styles/NewBuildingsPage.styles";

// Imagery reused from existing yacht photos until real build photos are added
// via the admin "Selected Projects" gallery.
const HERO_IMAGE =
  "/images/princess/X95/exterior/large/exterior-01_x95-slot-5-exterior-white-hull-6.webp";
const INTRO_IMAGE =
  "/images/princess/S80/exterior/large/exterior-01_s80-exterior-white-hull-1.webp";
const CTA_IMAGE =
  "/images/princess/Y80/exterior/large/exterior-04_y80-exterior-flybridge-1.webp";

// TODO: replace with real figures from the client.
const STATS = [
  { value: "20+", label: "Years of expertise" },
  { value: "40+", label: "Projects delivered" },
  { value: "15+", label: "Partner shipyards" },
];

const SERVICES = [
  "Contract & design review",
  "Technical advice & owner representation",
  "Shipyard & designer selection",
  "Quality and cost control",
  "On-site construction supervision",
  "Regular progress reports & photographic documentation",
  "Commissioning, sea trials & delivery",
];

const STEPS = [
  {
    title: "Project Manager",
    text: "We appoint a dedicated project manager, your single point of contact and advocate throughout the entire build.",
  },
  {
    title: "Design",
    text: "From first sketches to detailed naval architecture, we translate your vision into a coherent, buildable design.",
  },
  {
    title: "Specification",
    text: "A precise technical specification defines every system, material and finish before a single payment is made.",
  },
  {
    title: "Shipyard",
    text: "We help select the right shipyard for your project and negotiate a fair, watertight construction contract.",
  },
  {
    title: "The Build",
    text: "On-site supervision, quality inspections and cost control keep the project on spec, on schedule and on budget.",
  },
  {
    title: "Sea Trials & Delivery",
    text: "We oversee commissioning, sea trials, classification and a smooth handover of your finished yacht.",
  },
];

function NewBuildingsPage() {
  const dispatch = useDispatch();
  const { items: projects } = useSelector((state) => state.buildProjects);

  useEffect(() => {
    dispatch(fetchBuildProjects());
  }, [dispatch]);

  return (
    <>
      <PageHeader $image={mediaUrl(HERO_IMAGE)}>
        <Container>
          <StaggerReveal>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Build With Us</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <Title>New Buildings</Title>
            </MotionItem>
            <MotionItem $variants={fadeUpSmall}>
              <HeaderSub>
                Building a yacht is a creative and rewarding journey, and a
                complex one. We manage every stage on your behalf, from the
                first sketch to the day you step aboard.
              </HeaderSub>
            </MotionItem>
          </StaggerReveal>
        </Container>
      </PageHeader>

      <Section $bg="offWhite">
        <Container>
          <StatsRow>
            {STATS.map((stat) => (
              <StatItem key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </StatItem>
            ))}
          </StatsRow>
          <IntroGrid>
            <Lead>
              <SectionEyebrow>Yacht Building Management</SectionEyebrow>
              <p>
                A new build is the most personal way to own a yacht, with every
                detail configured to the way you cruise, entertain and live on
                board. It is also one of the most demanding projects an owner
                can undertake.
              </p>
              <p>
                Acting purely in your interest, our team represents you at the
                shipyard and protects your investment at every milestone, so
                you can enjoy the journey with complete confidence.
              </p>
              <IntroImage $src={mediaUrl(INTRO_IMAGE)} />
            </Lead>
            <ServiceList>
              {SERVICES.map((s) => (
                <ServiceItem key={s}>{s}</ServiceItem>
              ))}
            </ServiceList>
          </IntroGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionEyebrow>The Process</SectionEyebrow>
          <StepsGrid>
            {STEPS.map((step, i) => {
              const num = String(i + 1).padStart(2, "0");
              return (
              <StepCard key={step.title}>
                <StepGhost aria-hidden="true">{num}</StepGhost>
                <StepNumber>Step {num}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.text}</StepText>
              </StepCard>
              );
            })}
          </StepsGrid>
        </Container>
      </Section>

      {projects.length > 0 && (
        <Section $bg="offWhite">
          <Container>
            <SectionEyebrow>Selected Projects</SectionEyebrow>
            <ProjectsGrid as={StaggerReveal}>
              {projects.map((project) => (
                <MotionItem key={project.id} $variants={scaleIn}>
                  <ProjectCard>
                    <ProjectImage
                      $src={project.image ? mediaUrl(project.image) : null}
                    >
                      {!project.image && "Project"}
                    </ProjectImage>
                    <ProjectBody>
                      <ProjectMeta>
                        {[project.shipyard, project.year]
                          .filter(Boolean)
                          .join(" · ")}
                      </ProjectMeta>
                      <ProjectTitle>{project.title}</ProjectTitle>
                      {project.description && (
                        <ProjectDesc>{project.description}</ProjectDesc>
                      )}
                    </ProjectBody>
                  </ProjectCard>
                </MotionItem>
              ))}
            </ProjectsGrid>
          </Container>
        </Section>
      )}

      <CtaSection $src={mediaUrl(CTA_IMAGE)}>
        <Container>
          <Cta>
            <CtaTitle>Ready to start your build?</CtaTitle>
            <CtaText>
              Tell us about the yacht you have in mind and we will guide you
              through the next steps.
            </CtaText>
            <CtaButton to="/contact">Get in touch</CtaButton>
          </Cta>
        </Container>
      </CtaSection>
    </>
  );
}

export default NewBuildingsPage;
